import axiosInstance from '@/config/axios'
import { logError } from '@/utils/logger'
import { isAxiosError } from 'axios'

export interface UserPreference {
  id?: number
  preference_key: string
  preference_value: unknown
  created_at?: string
  updated_at?: string
}

// Cache global pour les préférences utilisateur
let preferencesCache: UserPreference[] | null = null
let cacheTimestamp: number | null = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
let fetchPromise: Promise<UserPreference[]> | null = null

export const userPreferencesService = {
  async getPreference(key: string): Promise<UserPreference | null> {
    try {
      // Vérifier que l'utilisateur est authentifié avant de faire l'appel API
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()
      
      if (!authStore.isAuthenticated || !authStore.accessToken) {
        return null
      }
      
      // Vérifier si le cache est valide
      const now = Date.now()
      if (preferencesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
        const preference = preferencesCache.find((pref: UserPreference) => pref.preference_key === key)
        if (!preference || preference.preference_value === undefined || preference.preference_value === null) {
          return null
        }
        return preference
      }
      
      // Si une requête est déjà en cours, attendre son résultat
      if (fetchPromise) {
        const data = await fetchPromise
        const preference = data.find((pref: UserPreference) => pref.preference_key === key)
        if (!preference || preference.preference_value === undefined || preference.preference_value === null) {
          return null
        }
        return preference
      }
      
      // Récupérer toutes les préférences de l'utilisateur
      fetchPromise = axiosInstance.get('/user-preferences/').then(response => response.data)
      const data = await fetchPromise
      fetchPromise = null
      
      if (!data || data.length === 0) {
        preferencesCache = []
        cacheTimestamp = now
        return null
      }
      
      // Mettre en cache
      preferencesCache = data
      cacheTimestamp = now
      
      // Filtrer manuellement pour trouver la préférence avec la clé demandée
      const preference = data.find((pref: UserPreference) => pref.preference_key === key)
      
      // Vérifier si la préférence existe (mais pas si preference_value est falsy, car false est une valeur valide)
      if (!preference || preference.preference_value === undefined || preference.preference_value === null) {
        return null
      }
      
      return preference
    } catch (error: unknown) {
      fetchPromise = null
      // Ne pas logger les erreurs 401 comme des erreurs critiques (utilisateur non authentifié)
      if (!isAxiosError(error) || error.response?.status !== 401) {
        logError(`Erreur lors de la récupération de la préférence: ${key}`, error)
      }
      return null
    }
  },

  async upsertPreference(key: string, value: unknown): Promise<UserPreference | null> {
    try {
      // S'assurer que la valeur n'est pas null ou undefined
      if (value === null || value === undefined) {
        logError(`Tentative d'enregistrement d'une valeur null ou undefined pour la clé: ${key}`)
        return null
      }
      
      const response = await axiosInstance.post('/user-preferences/upsert/', {
        preference_key: key,
        preference_value: value
      })
      
      // Invalider le cache après la mise à jour
      preferencesCache = null
      cacheTimestamp = null
      
      return response.data
    } catch (error: unknown) {
      logError(`Erreur lors de l'enregistrement de la préférence: ${key}`, error)
      
      // Afficher plus de détails sur l'erreur
      if (isAxiosError(error) && error.response) {
        logError('Détails de l\'erreur:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        })
      }
      
      return null
    }
  },
  
  // Méthode pour invalider manuellement le cache
  clearCache() {
    preferencesCache = null
    cacheTimestamp = null
    fetchPromise = null
  }
}
